import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { API } from "../../queries/api";
import ImageUpload from "../../components/image-upload/ImageUpload";
import {
	Avatar,
	Checkbox,
	Chip,
	ListItemAvatar,
	ListItemButton,
	ListItemText,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { InterestType, InterestUsersType, UserType } from "../../types";
import { CHAT_TYPES, IMAGE_TYPES } from "../../constants";
import useSnackbar from "../../hooks/useSnackbar";
import AddUsersByInterestsModal from "./AddUsersByInterestsModal";
import useSelfUser from "../../hooks/useSelfUser";
import { matrixClient } from "../../services/matrixClient";
import { collectMemberIds } from "./chatMembers";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
	props,
	ref
) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function CreateChatMatrix() {
	const [loading, setLoading] = useState(false);
	const { clubId } = useParams<{ clubId: string }>();
	const [image, setImage] = useState<string | null>(null);
	const [chatName, setChatName] = useState<string>("");
	const [chatDescription, setChatDescription] = useState<string>("");
	const [snackbarMsg, setSnackbarMsg] = useState("");
	const [includeAll, setIncludeAll] = useState(true);
	const [includeYourself, setIncludeYourself] = useState(true);
	const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);
	const [interests, setInterests] = useState<InterestType[]>([]);
	const [selectedInterests, setSelectedInterests] = useState<InterestType[]>(
		[]
	);
	const [chatType, setChatType] = useState<string>(CHAT_TYPES.CLUB.value);
	const [interestsWithUsers, setInterestWithUsers] = useState<
		InterestUsersType[]
	>([]);
	const [isAddUsersByInterestsModalOpen, setIsAddUsersByInterestsModalOpen] =
		useState(false);
	const { selfUser } = useSelfUser();
	const navigate = useNavigate();
	const openSnackbar = useSnackbar();

	// New state for redesign
	const [allClubUsers, setAllClubUsers] = useState<UserType[]>([]);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [userFilter, setUserFilter] = useState("");

	const handleCloseModal = () => {
		setIsAddUsersByInterestsModalOpen(false);
	};

	// Load all club members on mount
	const loadAllClubUsers = async () => {
		setIsLoadingUsers(true);
		try {
			const { data, status } = await API.post(
				`users/search?searchStr=&idClub=${clubId}`,
				{
					startDate: "2020-10-03T20:42:30.519Z",
					endDate: "2029-10-03T20:42:30.519Z",
					page: 0,
					size: 10000,
					reverse: true,
				}
			);
			if (status === 200 && data?.items?.length) {
				setAllClubUsers(data.items);
			}
		} catch (error) {
			console.log(error);
		}
		setIsLoadingUsers(false);
	};

	const getInterests = async () => {
		try {
			const { data, status } = await API.get(
				`keyword/PERSONAL_INTERESTS?idClub=${clubId}`
			);
			if (status === 200 && !!data.length) {
				setInterests(data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		loadAllClubUsers();
		getInterests();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Client-side filtering
	const filteredUsers = useMemo(() => {
		if (!userFilter.trim()) return allClubUsers;
		const q = userFilter.toLowerCase();
		return allClubUsers.filter((user) => {
			const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
			return fullName.includes(q);
		});
	}, [allClubUsers, userFilter]);

	const toggleUser = (user: UserType) => {
		const isSelected = selectedUsers.some((u) => u.id === user.id);
		if (isSelected) {
			setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
		} else {
			setSelectedUsers([...selectedUsers, user]);
		}
	};

	const toggleInterest = (interest: InterestType) => {
		const isSelected = selectedInterests.some((i) => i.id === interest.id);
		if (isSelected) {
			setSelectedInterests(
				selectedInterests.filter((i) => i.id !== interest.id)
			);
		} else {
			setSelectedInterests([...selectedInterests, interest]);
		}
	};

	const saveChat = async (usersByInterests: UserType[] = []) => {
		try {
			setLoading(true);

			const memberIDs = collectMemberIds({
				usersByInterests,
				selectedUsers,
				allClubUsers,
				self: selfUser,
				includeAll,
				includeYourself,
			});

			if (matrixClient.mode === "direct") {
				// Direct Synapse mode — map Bridges UUIDs to Matrix user IDs
				const matrixMemberIds = memberIDs.map((id) =>
					id.startsWith("@") ? id : `@bridges_${id}`
				);
				const roomId = await matrixClient.createRoom({
					name: chatName,
					description: chatDescription,
					image: image || undefined,
					memberIds: matrixMemberIds.length ? matrixMemberIds : undefined,
				});
				openSnackbar(`Chat created: ${roomId}`);
				setSnackbarMsg("Chat created successfully");
				navigate(`/club/${clubId}/chat/${encodeURIComponent(roomId)}`);
			} else {
				// Backend proxy mode — COMMUNITY chat
				const payload = {
					clubId: clubId,
					name: chatName,
					description: chatDescription,
					image: image,
					memberIDs: memberIDs.length ? memberIDs : undefined,
					includeAll: includeAll,
					chatPurpose: chatType,
				};
				const { data, status } = await API.post(`matrix/club_chat`, payload);
				if (status === 200 && !!data) {
					openSnackbar("Chat created successfully");
					setSnackbarMsg("Chat created successfully");
					navigate(`/club/${clubId}`);
				} else {
					setSnackbarMsg("Error creating chat");
				}
			}
		} catch (error) {
			console.log(error);
			setSnackbarMsg("Error creating chat");
		}
		setLoading(false);
	};

	const imageUploadHandle = (imageLink: string) => {
		setImage(imageLink);
	};

	const handleClose = (
		_event?: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") return;
		setSnackbarMsg("");
	};

	const includeAllHandle = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIncludeAll(event.target.checked);
	};

	const includeYourselfHandle = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setIncludeYourself(event.target.checked);
	};

	const getUsersByInterests = async () => {
		try {
			const { data, status } = await API.post(`users/interests`, {
				page: 0,
				size: 10000,
				interests: selectedInterests.map((interest) => interest.id),
				idClub: clubId,
			});
			if (status === 200 && !!data.items.length) {
				const interestsWithUsers = selectedInterests.reduce(
					(acc: any, interest: InterestType) => {
						const users = data.items.filter((user: UserType) => {
							const userInterestsIds = user.interests.map(
								(interest: any) => interest.id
							);
							return userInterestsIds.includes(interest.id);
						});
						const interestWithUsers = { ...interest, users };
						return [...acc, interestWithUsers];
					},
					[]
				);
				setInterestWithUsers(interestsWithUsers);
				setIsAddUsersByInterestsModalOpen(true);
			} else {
				setSnackbarMsg(
					"No users found with selected interests. Please try different interests."
				);
			}
		} catch (error) {
			console.log(error);
			setSnackbarMsg("Error finding users by interests.");
		}
	};

	const submitHandle = () => {
		// Community chats are the only server-supported kind here; private/DM
		// rooms are created by the mobile app, not the admin console.
		saveChat([]);
	};

	const handleChangeChatType = (event: SelectChangeEvent) => {
		setChatType(event.target.value as string);
	};

	const selectUsersByInterestsHandle = (users: UserType[]) => {
		const newSelectedUsers = [...selectedUsers, ...users];
		const uniqueSelectedUsers = Array.from(
			new Set(newSelectedUsers.map((user) => user.id))
		).map((id) => newSelectedUsers.find((user) => user.id === id));
		setSelectedUsers(uniqueSelectedUsers as UserType[]);
	};

	return (
		<Paper
			sx={{
				p: 2,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography component="h2" variant="h6" color="primary" gutterBottom>
				Create Chat (Matrix)
			</Typography>
			{loading && (
				<Box sx={{ width: "100%", my: 0.5 }}>
					<LinearProgress />
				</Box>
			)}
			<ImageUpload onUpload={imageUploadHandle} type={IMAGE_TYPES.CHAT_PHOTO} />
			{image && (
				<Avatar
					alt="Chat Avatar"
					src={image}
					sx={{ width: 100, height: 100, marginBottom: "16px" }}
				/>
			)}
			<Grid container spacing={2} sx={{ marginBottom: "8px" }}>
				<Grid item xs={4}>
					<TextField
						name="chat-name"
						label="Chat Name"
						fullWidth
						variant="outlined"
						value={chatName}
						onChange={(event) => setChatName(event.target.value)}
					/>
				</Grid>
				<Grid item xs={8}>
					<TextField
						name="description"
						label="Description"
						fullWidth
						multiline
						variant="outlined"
						value={chatDescription}
						onChange={(event) => setChatDescription(event.target.value)}
					/>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth>
						<InputLabel id="chat-type-label">Select channel type</InputLabel>
						<Select
							labelId="chat-type-label"
							id="chat-type-select"
							value={chatType}
							label="Select channel type"
							onChange={handleChangeChatType}
						>
							{Object.keys(CHAT_TYPES).map((key) => (
								<MenuItem
									key={key}
									value={CHAT_TYPES[key as keyof typeof CHAT_TYPES].value}
								>
									{CHAT_TYPES[key as keyof typeof CHAT_TYPES].label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<div>
				<FormControlLabel
					control={
						<Switch
							color="info"
							onChange={includeYourselfHandle}
							checked={includeYourself}
						/>
					}
					label="Add yourself to this chat"
				/>
			</div>
			<div>
				<FormControlLabel
					control={
						<Switch
							color="info"
							onChange={includeAllHandle}
							checked={includeAll}
						/>
					}
					label="Add all club users to this chat"
				/>
			</div>
			{!includeAll && (
				<div>
					{/* ===== Users: Scrollable checkbox list ===== */}
					<Typography variant="h6" gutterBottom>
						Add Users by Name
						{selectedUsers.length > 0 && (
							<Typography
								component="span"
								variant="body2"
								color="text.secondary"
								sx={{ ml: 1 }}
							>
								({selectedUsers.length} selected)
							</Typography>
						)}
					</Typography>
					<TextField
						label="Filter users..."
						fullWidth
						variant="outlined"
						size="small"
						value={userFilter}
						onChange={(e) => setUserFilter(e.target.value)}
						sx={{ mb: 1 }}
					/>
					{isLoadingUsers ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
							<CircularProgress size={28} />
						</Box>
					) : (
						<Box
							sx={{
								maxHeight: 250,
								overflow: "auto",
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 1,
								mb: 2,
							}}
						>
							{filteredUsers.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ p: 2, textAlign: "center" }}
								>
									No users found
								</Typography>
							) : (
								filteredUsers.map((user) => {
									const isSelected = selectedUsers.some(
										(u) => u.id === user.id
									);
									return (
										<ListItemButton
											key={user.id}
											onClick={() => toggleUser(user)}
											dense
											sx={{ py: 0.5 }}
										>
											<Checkbox
												checked={isSelected}
												size="small"
												sx={{ mr: 1 }}
												tabIndex={-1}
												disableRipple
											/>
											<ListItemAvatar sx={{ minWidth: 40 }}>
												<Avatar
													src={user.imageLink}
													sx={{ width: 32, height: 32 }}
												>
													{user.firstName?.[0]}
												</Avatar>
											</ListItemAvatar>
											<ListItemText
												primary={`${user.firstName} ${user.lastName}`}
											/>
										</ListItemButton>
									);
								})
							)}
						</Box>
					)}

					{/* ===== Interests: Clickable chip toggles ===== */}
					<Typography variant="h6" gutterBottom>
						Add users by Interests
						{selectedInterests.length > 0 && (
							<Typography
								component="span"
								variant="body2"
								color="text.secondary"
								sx={{ ml: 1 }}
							>
								({selectedInterests.length} selected)
							</Typography>
						)}
					</Typography>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 1,
							mb: 2,
						}}
					>
						{interests.map((interest) => {
							const isSelected = selectedInterests.some(
								(i) => i.id === interest.id
							);
							return (
								<Chip
									key={interest.id}
									label={interest.word}
									variant={isSelected ? "filled" : "outlined"}
									color={isSelected ? "primary" : "default"}
									onClick={() => toggleInterest(interest)}
									clickable
								/>
							);
						})}
					</Box>
					{selectedInterests.length > 0 && (
						<Button
							variant="contained"
							color="info"
							onClick={getUsersByInterests}
							sx={{ mb: 2 }}
						>
							Find users by selected interests
						</Button>
					)}
				</div>
			)}
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					paddingTop: "16px",
				}}
			>
				<Button variant="contained" color="primary" onClick={submitHandle}>
					Create chat
				</Button>
			</div>

			<Snackbar
				open={!!snackbarMsg}
				autoHideDuration={6000}
				onClose={handleClose}
			>
				<Alert onClose={handleClose} severity="info" sx={{ width: "100%" }}>
					{snackbarMsg}
				</Alert>
			</Snackbar>
			<AddUsersByInterestsModal
				isOpen={isAddUsersByInterestsModalOpen}
				interests={interestsWithUsers}
				onClose={handleCloseModal}
				onConfirm={selectUsersByInterestsHandle}
			/>
		</Paper>
	);
}
